import { useEffect, useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { datadogLogs } from '@datadog/browser-logs';
import { useAppStore } from '@/stores/app-store';
import fLogger from '@fintronners/react-utils/src/fLogger';

const getFirstPartyHosts = () => {
  const urls = [process.env.GRAPHQL_API_URL, process.env.NEW_API_URL, process.env.AUTH_API_URL];

  return urls.filter((host): host is string => Boolean(host));
};

/**
 * Initializes Datadog Real User Monitoring (RUM).
 * This component should be included in the root of the application to enable
 * client-side monitoring of user interactions, performance metrics, and errors.
 *
 * @see https://docs.datadoghq.com/real_user_monitoring/guide/monitor-your-nextjs-app-with-rum/
 */
export const useDatadog = () => {
  const userDetails = useAppStore((state) => state.userDetails);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const env = process.env.NEXT_PUBLIC_DATADOG_ENV || 'dev';
    const applicationId = process.env.NEXT_PUBLIC_DATADOG_CLIENT_APPLICATION_ID;
    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
    const version = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.slice(0, 7);
    const service = window.location.hostname;
    const site = 'datadoghq.com'; // US1 site

    if (!applicationId || !clientToken) {
      fLogger.warn('Datadog RUM not initialized: Missing environment variables');
      return;
    }

    if (datadogRum.getInitConfiguration()) {
      setIsInitialized(true);
      fLogger.info('Datadog RUM already initialized');
      return;
    }

    try {
      datadogLogs.init({
        env,
        version,
        site,
        service,
        clientToken,
        forwardConsoleLogs: ['error', 'warn'],
        useSecureSessionCookie: true,
        sessionSampleRate: 100,
        telemetrySampleRate: 100,
        telemetryUsageSampleRate: 100,
        telemetryConfigurationSampleRate: 100,
      });

      datadogRum.init({
        site,
        env,
        version,
        service,
        clientToken,
        applicationId,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 100,
        traceSampleRate: 100,
        trackUserInteractions: true,
        trackResources: true, // Track resources (XHR, Fetch, images, etc.)
        trackLongTasks: true, // Track tasks that block the main thread for >50ms
        trackViewsManually: false,
        startSessionReplayRecordingManually: false,
        defaultPrivacyLevel: 'mask-user-input',
        // Specify URLs to propagate trace headers for connection between RUM and backend trace
        // This allows correlation between frontend and backend traces
        allowedTracingUrls: getFirstPartyHosts(),
        plugins: [reactPlugin({ router: false })],
        useSecureSessionCookie: true,
      });

      datadogRum.onReady(() => {
        setIsInitialized(true);

        const context = datadogRum.getInternalContext();

        if (context?.session_id) {
          fLogger.info(`Datadog RUM session started: ${context.session_id}`);
        }

        fLogger.info(`Datadog RUM initialized for ${service} in ${env} environment`);
      });
    } catch (error) {
      fLogger.error('Failed to initialize Datadog RUM:', error);
    }
  }, []);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (!userDetails) {
      fLogger.info('Datadog RUM user removed');
      return;
    }

    const user = {
      id: userDetails.id,
      email: userDetails?.profile?.email,
      name: `${userDetails?.profile?.firstName} ${userDetails?.profile?.lastName}`,
    };

    datadogLogs.setUser(user);
    datadogRum.setUser(user);

    fLogger.info(`Datadog RUM user set: ${user.id}`);
  }, [userDetails, isInitialized]);

  return null;
};
